// @ts-check

import EmblaCarousel from "embla-carousel";
// Embla plugins
import Accessibility from "embla-carousel-accessibility";
import Autoplay from "embla-carousel-autoplay";
import ClassNames from "embla-carousel-class-names";

// TODO: Option to set this on instantiation?
const DEFAULT_SLIDE_DURATION = 6; // When no duration is set, this is the fallback.

const carousels = document.querySelectorAll(".wp-block-group.iop-carousel");

// Track visible carousels
const visibleCarousels = new Set();

for (const wrapperNode of carousels) {
  const emblaOptions = {
    loop: true,
    duration: 50,
    container: ".iop-carousel__container",
    slide: ".iop-carousel__slide",
  };

  /** @type {HTMLElement | null} */
  const viewportNode = wrapperNode.querySelector(".iop-carousel__viewport");
  if (!viewportNode) {
    console.warn("No viewport found for carousel", wrapperNode);
    continue;
  }

  const slides = viewportNode.querySelectorAll(emblaOptions.slide);
  if (slides.length < 2) {
    console.warn("Not enough slides to init carousel", wrapperNode);
    continue;
  }

  // inject navigation dots
  const dotsNode = document.createElement("div");
  dotsNode.className = "iop-carousel-dotnav";
  wrapperNode.appendChild(dotsNode);

  const emblaApi = EmblaCarousel(viewportNode, emblaOptions, [
    Accessibility({
      announceChanges: true,
      rootNode: (emblaRoot) => emblaRoot.parentElement,
    }),
    ClassNames(),
    Autoplay({
      delay: (_snaps, api) =>
        api.slideNodes().map((slide) => {
          let seconds = Number(slide.dataset.slideDuration);
          if (!Number.isFinite(seconds) || seconds <= 0) {
            seconds = DEFAULT_SLIDE_DURATION;
          }
          return seconds * 1000;
        }),
    }),
  ]);

  const createPrevNextArrows = () => {
    const arrows = document.createElement("div");
    arrows.className = "iop-carousel-arrows";
    const prevButton = document.createElement("button");
    const nextButton = document.createElement("button");
    prevButton.className = "iop-carousel-arrow iop-carousel-arrow--prev";
    nextButton.className = "iop-carousel-arrow iop-carousel-arrow--next";
    prevButton.addEventListener("click", () => {
      emblaApi.plugins().autoplay?.reset();
      emblaApi.goToPrev();
    });
    nextButton.addEventListener("click", () => {
      emblaApi.plugins().autoplay?.reset();
      emblaApi.goToNext();
    });

    arrows.appendChild(prevButton);
    arrows.appendChild(nextButton);

    wrapperNode.prepend(arrows);

    // touchstart is a hack to enable :active states in CSS
    arrows.addEventListener("touchstart", () => {}, { passive: true });
  };

  const createDotNav = () => {
    const slideList = emblaApi.snapList();

    dotsNode.innerHTML = ""; // Clear existing dot navigation children
    slideList.forEach((slide, index) => {
      const dotButton = document.createElement("button");
      dotButton.className = `${dotsNode.className}__dot`;
      dotButton.addEventListener("click", () => {
        emblaApi.plugins().autoplay?.stop();
        emblaApi.goTo(index);
      });
      wrapperNode
        .querySelector(`.${dotsNode.className}`)
        ?.appendChild(dotButton);
    });
  };

  const createA11yLiveArea = () => {
    const liveRegion = document.createElement("div");
    liveRegion.className = "iop-carousel__live a11y sr-only";
    wrapperNode.appendChild(liveRegion);
  };

  /**
   * Updates the visual state of navigation dots to reflect the currently selected slide
   * @param {number} selectedIndex - The index of the currently selected slide
   * @returns {void}
   */
  const updateDotState = (selectedIndex) => {
    const dotButtons = wrapperNode.querySelectorAll(
      `.${dotsNode.className}__dot`,
    );
    dotButtons.forEach((button, index) => {
      button.classList.remove("is-active");
      if (index === selectedIndex) {
        button.classList.add("is-active");
      }
    });
  };

  /** @param {import("embla-carousel").EmblaCarouselType} emblaApi */
  const updateA11y = (emblaApi) => {
    const accessibility = emblaApi.plugins().accessibility;
    if (!accessibility) return;

    accessibility.setupLiveRegion(".iop-carousel__live");
    accessibility.setupDotButtons(".iop-carousel-dotnav__dot");
    accessibility.setupPrevAndNextButtons(
      ".iop-carousel-arrow--prev",
      ".iop-carousel-arrow--next",
    );
  };

  // IntersectionObserver to track visibility
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        visibleCarousels.add(emblaApi);
      } else {
        visibleCarousels.delete(emblaApi);
      }
    });
  });
  observer.observe(wrapperNode);

  emblaApi.on("reinit", () => {
    console.log("Embla re-initialized");

    createDotNav();
    emblaApi.plugins().autoplay?.play();
  });
  emblaApi.on("select", (arg) => {
    // console.log("selected", arg);
    updateDotState(emblaApi.selectedSnap());
  });

  createPrevNextArrows();
  createDotNav();
  createA11yLiveArea();
  updateDotState(emblaApi.selectedSnap());
  updateA11y(emblaApi);
  emblaApi.plugins().autoplay?.play();
}

// Global keydown listener for arrow keys: advance every visible carousel.
// Skip when focus is in a text input, textarea, or contenteditable so typing
// isn't intercepted by carousel navigation.
window.addEventListener("keydown", (e) => {
  if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
  if (
    e.target instanceof HTMLElement &&
    (e.target.matches("input, textarea") || e.target.isContentEditable)
  ) {
    return;
  }
  e.preventDefault();
  for (const api of visibleCarousels) {
    api.plugins().autoplay?.reset();
    if (e.key === "ArrowLeft") {
      api.goToPrev();
    } else {
      api.goToNext();
    }
  }
});
